import React, {
  forwardRef,
  useEffect,
  useContext,
  useState,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import debounce from "lodash/debounce";

import { Button } from "../Button";
import { CopyText } from "../CopyText";
import { ThemeContext } from "../Theme";

import StickyPortal from "./StickyPortal";
import styles from "./StickySalesBar.scss";

export const StickySalesBar = forwardRef(
  (
    {
      children,
      isHidden,
      zIndex,
      customClass,
      wrapperProps,
      stickyDirection,
      aligningElementRef,
      ...restProps
    },
    ref
  ) => {
    const [positionY, setPositionY] = useState(0);
    const theme = useContext(ThemeContext);

    function calculateVerticalPosition() {
      if (typeof document === "undefined") return;
      if (!aligningElementRef) return;
      if (!aligningElementRef.current) return;
      let position = 0;

      if (stickyDirection === "top") {
        const visibleTopElementPortion =
          aligningElementRef.current.getBoundingClientRect().top +
          aligningElementRef.current.offsetHeight;

        if (visibleTopElementPortion > 0) {
          position = visibleTopElementPortion;
        }
      } else {
        const visibleBottomElementPortion =
          aligningElementRef.current.getBoundingClientRect().top +
          window.scrollY -
          (window.innerHeight + window.scrollY);

        if (visibleBottomElementPortion < 0) {
          position = visibleBottomElementPortion;
        }
      }

      setPositionY(position);
    }

    const debouncedCalculateVerticalPosition = useCallback(
      debounce(calculateVerticalPosition, 200),
      [stickyDirection]
    );

    useEffect(() => {
      calculateVerticalPosition();

      document.addEventListener("scroll", calculateVerticalPosition);
      window.addEventListener("resize", debouncedCalculateVerticalPosition);

      return () => {
        document.removeEventListener("scroll", calculateVerticalPosition);
        window.addEventListener("resize", debouncedCalculateVerticalPosition);
      };
    }, [stickyDirection, calculateVerticalPosition]);

    const innerStyles = {
      zIndex,
      transform: `translate3d(0, ${positionY}px, 0)`,
      top: stickyDirection === "top" ? 0 : null,
      bottom: stickyDirection === "bottom" ? 0 : null,
    };

    return (
      <div ref={ref} className={styles.base} {...wrapperProps}>
        <div
          className={cx(styles.bar, styles[theme], customClass, {
            [styles["bar--hidden"]]: isHidden,
            [styles["bar--bottom"]]: stickyDirection === "bottom",
          })}
          style={innerStyles}
          {...restProps}
        >
          <div className={styles.bar__content}>{children}</div>
        </div>
      </div>
    );
  }
);

StickySalesBar.Copy = (props) => (
  <CopyText variants={[CopyText.Variant.highlight]} {...props} />
);
StickySalesBar.Copy.displayName = "StickySalesBar.Copy";
StickySalesBar.Portal = StickyPortal;
StickySalesBar.Portal.displayName = "StickySalesBar.Portal";
StickySalesBar.Button = forwardRef((props, ref) => (
  <Button ref={ref} {...props} />
));
StickySalesBar.Button.displayName = "StickySalesBar.Button";

StickySalesBar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  customClass: PropTypes.string,
  wrapperProps: PropTypes.objectOf(PropTypes.any),
  isHidden: PropTypes.bool,
  zIndex: PropTypes.number,
  stickyDirection: PropTypes.oneOf(["top", "bottom"]),
  aligningElementRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current:
        typeof Element !== "undefined"
          ? PropTypes.instanceOf(Element)
          : PropTypes.number,
    }),
  ]),
};

StickySalesBar.defaultProps = {
  customClass: null,
  wrapperProps: null,
  isHidden: false,
  zIndex: 1,
  stickyDirection: "top",
  aligningElementRef: null,
};

StickySalesBar.displayName = "StickySalesBar";

export default StickySalesBar;
